import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import './BiohackRating.css';
import { Icon } from './Icon.js';
import { BiohackRatingService } from '../utils/BiohackRatingService';

const BiohackRating = ({ biohack, onRatingSubmitted, onClose }) => {
  const [ratings, setRatings] = useState({
    ease_of_use_score: 0,
    affordability_score: 0,
    time_efficiency_score: 0
  });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);

  // Get current user on component mount
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      console.log('Current user:', user);
      if (user) {
        setUserId(user.id);
        console.log('User ID set to:', user.id);
      } else {
        console.log('No user found');
      }
    };
    getCurrentUser();
  }, [biohack.id]);

  // Handle rating change
  const handleRatingChange = (criteria, value) => {
    setRatings(prev => ({
      ...prev,
      [criteria]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('Form submission started');
    console.log('Current userId:', userId);
    console.log('Current ratings:', ratings);
    
    if (!userId) {
      setError('Please sign in to rate biohacks');
      return;
    }

    // Validate that all ratings are provided
    const hasAllRatings = Object.values(ratings).every(score => score > 0);
    if (!hasAllRatings) {
      setError('Please provide ratings for all criteria');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const ratingData = {
        user_id: userId,
        hack_id: biohack.id,
        ...ratings,
        timestamp: new Date().toISOString()
      };

      console.log('Submitting rating data:', ratingData);
      const result = await BiohackRatingService.submitRating(ratingData);

      if (!result) throw new Error('Failed to submit rating');

      // Show success message
      if (onRatingSubmitted) {
        onRatingSubmitted(ratings);
      }

      // Close the rating modal
      if (onClose) {
        onClose();
      }
    } catch (err) {
      console.error('Error submitting rating:', err);
      setError('Failed to submit rating. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Render star rating input
  const renderStarRating = (criteria, label, description) => {
    const currentValue = ratings[criteria];
    
    return (
      <div className="rating-criteria">
        <div className="rating-header">
          <label className="rating-label">{label}</label>
          <span className="rating-description">{description}</span>
        </div>
        <div className="star-rating-container">
          <span className="scale-label-left">Least</span>
          <div className="star-rating">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                className={`star-btn ${star <= currentValue ? 'filled' : ''}`}
                onClick={() => handleRatingChange(criteria, star)}
                title={`${star} ${star === 1 ? 'star' : 'stars'}`}
              >
                <Icon 
                  name="Star" 
                  className={`star-icon ${star <= currentValue ? 'filled' : ''}`}
                />
              </button>
            ))}
          </div>
          <span className="scale-label-right">Most</span>
        </div>
      </div>
    );
  };

  if (!biohack) return null;

  return (
    <div className="biohack-rating-overlay" onClick={(e) => e.target === e.currentTarget && onClose?.()}>
      <div className="biohack-rating-modal" onClick={(e) => e.stopPropagation()}>
        <div className="biohack-rating-modal-header">
          <h3>Rate this biohack</h3>
          <button
            type="button"
            className="biohack-rating-close-btn"
            onClick={onClose}
            title="Close"
          >
            <Icon name="X" className="icon--sm" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="biohack-rating-modal-body">

          {renderStarRating(
            'ease_of_use_score', 
            'Ease of Use', 
            'How easy was this biohack to use?'
          )}
          
          {renderStarRating(
            'time_efficiency_score', 
            'Time Efficiency', 
            'How time-efficient was this biohack?'
          )}
          
          {renderStarRating(
            'affordability_score', 
            'Affordability', 
            'How affordable was this biohack?'
          )}

          {error && (
            <div className="rating-error">
              {error}
            </div>
          )}

          <div className="biohack-rating-actions">
            <button
              type="submit"
              className="btn btn--primary btn--md btn--rounded"
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit Rating'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BiohackRating;
